/* eslint-disable no-param-reassign */
import { useState, useEffect, useCallback, useMemo } from 'react';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import Modal from '@deprecated-ui/common/Modal';
import color from '@deprecated-ui/_style/color';
import debounce from 'lodash.debounce';
import useToggle from 'hooks/useToggle';
import If from 'global/ErrorHandlers/If';
import Image from 'global/Image';
import {
  trackAutoSellAlterOrderView,
  trackAutoSellFormView,
  trackAutoSellInfoSelected,
  trackAutoSellInfoView,
  trackAutoSellOrderCanceled,
  trackAutoSellOrderConfirmed,
  trackAutoSellOrderInfoClosed,
  trackAutoSellOrderQuantityAdjusted,
} from 'lib/analytics/autoOrder';
import { lotSize } from 'global/TradingModal/constant';
import { formatNumber } from 'utils/number';
import tradingDataStore from 'lib/stores/tradingData';
import alertMessage from 'global/AlertMessage';
import InputStepper from 'global/Securities/components/InputStepper';
import {
  getFraction,
  getStepFractionByValue,
} from 'global/Securities/utils/transactionPrice';
import { SmartOrderCode } from 'global/Securities/types/transaction';
import { smartOrderText } from 'global/Securities/constants/transactionForm';
import { PortfolioStateItem } from 'global/Securities/types/store/portfolio';

import useTradingStore from 'features/transaction/useTradingStore';
import { SmartOrderResponseItem } from 'global/Securities/types/api/smartOrder';
import tradingOperationStore from 'lib/stores/tradingOperation';
import { ModalButtonContainer, ModalTradingButton } from '../styled';
import store from './store';

import { generateTradingVariableGeneral } from './util';
import { iModal } from '../types';

interface iFormModal extends iModal {
  type: SmartOrderCode;
  portfolio: Partial<PortfolioStateItem> | Record<string, never>;
  onDelete: (data: SmartOrderResponseItem) => void;
  afterSubmit: () => void;
}

export const FormModal = ({
  type,
  visible,
  onCancel,
  portfolio,
  onDelete,
  afterSubmit,
}: iFormModal) => {
  const [isVisible, toggle] = useToggle();
  const { available_lot, price_average_fee } = portfolio;

  const autoOrderId = useTradingStore((s) => s.autoOrderId);
  const autoOrderStatus = useTradingStore((s) => s.autoOrderStatus);
  const priceOrder = useTradingStore((s) => s.priceOrder);
  const orderOpen = useTradingStore((s) => s.orderOpen);
  const { stockData, addSmartOrder, editSmartOrder, isLoading } = store();
  const configFormula = tradingDataStore((s) => s.configFormula.data);

  const { postTradeAmend } = tradingOperationStore();

  const [lot, setLot] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);

  const autoOrderData = useMemo(
    () => stockData.find((item) => item.id === autoOrderId),
    [stockData],
  );

  const isAmendAutoOrderOpen = !!autoOrderId && autoOrderStatus === 'OPEN';

  const availableLot = useMemo(() => {
    const roundedAvailableLot = Math.floor(available_lot);
    if (
      isAmendAutoOrderOpen &&
      typeof roundedAvailableLot === 'number' &&
      !Number.isNaN(roundedAvailableLot)
    ) {
      return roundedAvailableLot + Number(orderOpen);
    }
    return available_lot;
  }, [autoOrderData, available_lot, orderOpen, isAmendAutoOrderOpen]);

  useEffect(() => {
    if (autoOrderData && !isLoading) {
      setLot(Number(autoOrderData.shares) / lotSize);
      setPrice(autoOrderData.price);
    }
  }, [autoOrderData, isLoading]);

  useEffect(() => {
    // Amend auto order with open status
    if (isAmendAutoOrderOpen) {
      setLot(Number(orderOpen));
      setPrice(Number(priceOrder));
    }
  }, [autoOrderId, autoOrderStatus, orderOpen, priceOrder]);

  useEffect(() => {
    if (visible) {
      if (autoOrderId) {
        trackAutoSellAlterOrderView('Edit');
      } else {
        trackAutoSellFormView(smartOrderText[type]);
      }
    }
  }, [visible]);

  useEffect(() => {
    if (isVisible) {
      trackAutoSellInfoView(smartOrderText[type]);
    }
  }, [isVisible]);

  const data = {
    [SmartOrderCode.StopLoss]: {
      image: '/images/stop-loss.svg',
      text: 'adalah tindakan untuk menjual saham secara otomatis di harga tertentu untuk membatasi kerugian.',
    },
    [SmartOrderCode.TakeProfit]: {
      image: '/images/take-profit.svg',
      text: 'adalah tindakan untuk mengambil profit kamu secara otomatis setelah harga saham mencapai target yang kamu inginkan.',
    },
  };

  const handleLotChange = (value: number) => {
    const roundedAvailableLot = Math.floor(availableLot);

    if (Number.isNaN(value)) {
      setLot(roundedAvailableLot);
      return;
    }

    if (value > roundedAvailableLot) {
      setLot(roundedAvailableLot);
      alertMessage({
        alertType: 'snackbar',
        messageType: 'error',
        content: 'Jumlah Lot tidak bisa melebihi Available Lot',
        hasCloseIcon: true,
      });
      return;
    }

    setLot(value);
  };

  const getStepValidation = (value: number) => {
    const { up } = getFraction(value);

    if (value % up !== 0) {
      alertMessage({
        alertType: 'snackbar',
        messageType: 'error',
        content: 'Harga disesuaikan berdasarkan fraksi',
        hasCloseIcon: true,
      });

      return value - (value % up);
    }

    return value;
  };

  const handlePriceChangeValidation = useCallback(
    debounce((value: number) => {
      const valueWithStep = getStepValidation(value);
      if (valueWithStep !== value) {
        setPrice(valueWithStep);
      }
    }, 300),
    [],
  );

  const handlePriceChange = (value: number) => {
    setPrice(value);

    if (value > 0) handlePriceChangeValidation(value);
    trackAutoSellOrderQuantityAdjusted();
  };

  const handlePriceBlur = () => {
    const newPrice = getStepValidation(price);
    if (price !== newPrice) {
      setPrice(newPrice);
      trackAutoSellOrderQuantityAdjusted();
    }
  };

  const onSubmit = async () => {
    if (autoOrderId) {
      if (autoOrderStatus === 'OPEN') {
        const dataOrder = {
          price,
          shares: lot * lotSize,
          order_id: autoOrderId,
        };
        await postTradeAmend([dataOrder]);
      } else {
        await editSmartOrder(
          {
            id: autoOrderId,
            shares: (lot * lotSize)?.toString(),
            price,
          },
          smartOrderText[type],
        );
      }

      afterSubmit();
    } else {
      await addSmartOrder(
        {
          stock_code: portfolio.symbol,
          shares: (lot * lotSize)?.toString(),
          price,
          type,
        },
        smartOrderText[type],
      );
      afterSubmit();
    }

    const { SELL_GAIN } = generateTradingVariableGeneral(
      configFormula,
      lot,
      price,
      price_average_fee,
      portfolio.symbol,
    );

    trackAutoSellOrderConfirmed(
      smartOrderText[type],
      portfolio.symbol,
      lot,
      price,
      `${formatNumber(SELL_GAIN, true, '0.0')}%`,
    );
  };

  return (
    <>
      <Modal
        visible={visible && !isVisible}
        onCancel={() => {
          onCancel();
          trackAutoSellOrderCanceled();
        }}
        centered
        width={520}
      >
        {/* @ts-expect-error */}
        <Flex width="100%" p="16px" align="center">
          <If condition={autoOrderId}>
            {/* @ts-expect-error */}
            <Text
              as="button"
              className="button-unstyled"
              color={color.sunsetOrange}
              size="18px"
              family="bold"
              ml="4px"
              onClick={() => onDelete(autoOrderData)}
            >
              Delete
            </Text>
          </If>
          <Flex align="center" justify="center" width="100%">
            <Text
              align="center"
              family="bold"
              size="18px"
              // @ts-expect-error
              mr="5px"
              ml={autoOrderId ? '-55px' : '0'}
            >
              {smartOrderText[type]}
            </Text>
            <button
              className="button-unstyled"
              onClick={() => {
                toggle();
                trackAutoSellInfoSelected(smartOrderText[type]);
              }}
              type="button"
            >
              <Image src="/icon/info.svg" width="12px" />
            </button>
          </Flex>
        </Flex>
        {/* @ts-expect-error */}
        <Flex align="center" justify="space-between" p="8px 20px" width="100%">
          <Text size="16px">Lot</Text>
          <InputStepper
            containerStyle={{ width: 160 }}
            min={0}
            max={availableLot}
            value={lot}
            onChange={handleLotChange}
            disabled={isLoading}
          />
        </Flex>
        {/* @ts-expect-error */}
        <Flex align="center" justify="space-between" p="8px 20px 20px 20px">
          <Text size="16px">Price</Text>
          <InputStepper
            containerStyle={{ width: 160 }}
            value={price}
            min={0}
            max={Number.MAX_SAFE_INTEGER}
            step={getStepFractionByValue(price)}
            onChange={handlePriceChange}
            onBlur={handlePriceBlur}
            disabled={isLoading}
            disableDecimal
          />
        </Flex>

        <ModalButtonContainer>
          <ModalTradingButton
            className={lot === 0 || price === 0 ? 'disabled' : 'exercise'}
            disabled={lot === 0 || price === 0}
            onClick={lot === 0 || price === 0 ? null : onSubmit}
            loading={isLoading}
          >
            Submit
          </ModalTradingButton>
        </ModalButtonContainer>
      </Modal>
      <Modal
        visible={isVisible}
        onCancel={() => {
          toggle();
          trackAutoSellOrderInfoClosed();
        }}
        centered
        width={520}
      >
        {/* @ts-expect-error */}
        <Text align="center" family="bold" size="18px" p="16px" pb="0">
          {smartOrderText[type]}
        </Text>
        <div style={{ padding: 16 }}>
          <Flex align="center" justify="center">
            <Image src={data[type].image} />
          </Flex>
          <Text as="p">
            <Text family="bold" as="span">
              {smartOrderText[type]}
            </Text>{' '}
            {data[type].text}
          </Text>
        </div>
      </Modal>
    </>
  );
};

export default FormModal;
